import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useGetAgencyUploadedFileList } from '@nxsys/hooks/api/dashboard';
import { IAgencyUpload } from '@nxsys/types/models/agencyUpload';
import { formatStatus } from '@nxsys/utils';
import { ColumnsType } from 'antd/lib/table';
import { find } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { agencyUploadTypeOptions } from '../../import/constants';
import { Icons } from '@nxsys/types/icons';
import ViewDetailAgencyFileModal from './ViewDetailAgencyFileModal';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  agencyId: string;
};

const ViewAgencyUploadedFileModal: React.FC<Props> = ({ isOpen, toggle, agencyId }) => {
  const { value, loading, run } = useGetAgencyUploadedFileList();
  const data = useMemo(() => value?.data ?? [], [value]);

  const [isOpenViewDetailModal, setIsOpenViewDetailModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IAgencyUpload | null>(null);

  useEffect(() => {
    if (isOpen)
      run({ agencyIds: [agencyId], agencyRefs: [agencyId] });
  }, [isOpen]);

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      run({
        agencyIds: [agencyId],
        agencyRefs: [agencyId],
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const handleOpenViewDetailModal = (file: IAgencyUpload) => {
    setSelectedFile(file);
    setIsOpenViewDetailModal(true);
  };
  
  const columns: ColumnsType<IAgencyUpload> = [
    {
      key: 'action',
      title: 'Action',
      fixed: 'left',
      align: 'left',
      render: (record: any) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => handleOpenViewDetailModal(record)}>
            <Icons.IconEye />
          </li>
          <li onClick={() => window.open(record.attachmentURL, '_blank')}>
            <Icons.IconDownload />
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'status',
      title: 'Status',
      align: 'center',
      dataIndex: 'status',
      render: (value) => (
        <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
          {formatStatus(value)}
        </AppTagStatus>
      ),
      width: 150,
      sorter: true,
    },
    {
      key: 'createdAt',
      title: 'Date',
      align: 'right',
      dataIndex: 'createdAt',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      width: 150,
    },
    {
      key: 'uploadBy',
      title: 'Upload By',
      dataIndex: 'author',
      render: (value) => `${value?.forename || ''} ${value?.surname || ''}`,
    },
    {
      key: 'fileType',
      title: 'File Type',
      dataIndex: 'fileType',
      render: (value) =>
        find(agencyUploadTypeOptions, (val) => val.value === value)?.label ||
        value,
    },
    {
      key: 'fileName',
      title: 'File Name',
      dataIndex: 'fileName',
    },
    {
      key: 'note',
      title: 'Notes',
      dataIndex: 'note',
      width: 400,
    },
    {
      key: 'cancelReason',
      title: 'Notes from Company',
      dataIndex: 'cancelReason',
      width: 300,
    },
  ];

  return (
    <AppModalContainer
      title="View Files"
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
    >
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={data as IAgencyUpload[]}
          loading={loading}
          pagination={false}
          page={1}
          total={value?.totalDocs ?? 0}
          pageSize={value?.totalDocs ?? 0}
          scroll={{ x: 1800 }}
          columns={columns}
          onChangeTable={onChangeTable}
        />
      </AppsContent>
      <ViewDetailAgencyFileModal
        isOpen={isOpenViewDetailModal}
        toggle={() => setIsOpenViewDetailModal(false)}
        url={selectedFile?.attachmentURL ?? ''}
      />
    </AppModalContainer>
  );
};

export default ViewAgencyUploadedFileModal;
